import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Typography, Divider } from "@mui/material";
import PropTypes from "prop-types";
import "./CustomDropdown.css";
import ArrowIcon from "../assets/drop-down.svg";

interface Option {
  value: string;
  label: React.ReactNode;
  icon?: string;
}

interface CustomDropdownProps {
  label: string;
  value: string;
  options: Option[];
  onChange: (selectedValue: string) => void;
  menuPlacement?: "above" | "below";
  customStyles?: {
    button?: React.CSSProperties;
    buttonClass?: string;
    label?: React.CSSProperties;
    labelClass?: string;
    option?: React.CSSProperties;
    optionClass?: string;
    menu?: React.CSSProperties;
    menuClass?: string;
  };
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  label,
  value,
  options,
  onChange,
  menuPlacement = "below",
  customStyles = {},
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => setIsOpen((prev) => !prev);

  const handleOptionClick = (selectedValue: string) => {
    onChange(selectedValue);
    setIsOpen(false);
  };
  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    // Add event listener for clicks outside the dropdown
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Cleanup the event listener
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const selectedOption = options.find((option) => option.value === value);
  const displayLabel = selectedOption ? selectedOption.label : label;

  return (
    <Box
      className={`custom-dropdown ${customStyles.menuClass || ""}`}
      ref={dropdownRef}
    >
      <Button
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          height: "inherit",
          borderRadius: "4px",
          padding: "10px 16px",
          border: "1px solid #64748b",
          ...customStyles.button, // Apply custom styles
        }}
        className={`dropdown-toggle ${customStyles.buttonClass || ""}`}
        onClick={handleToggle}
      >
        <span
          className={customStyles.labelClass || ""}
          style={{ fontSize: "14px", ...customStyles.label }}
        >
          {displayLabel}
        </span>
        <Box
          sx={{
            transform: isOpen ? "rotate(0deg)" : "rotate(180deg)",
            transition: "transform 0.3s ease",
          }}
        >
          <img
            src={ArrowIcon}
            alt="custom-arrow-icon"
            style={{ width: "10px", height: "10px" }}
          />
        </Box>
      </Button>
      {isOpen && (
        <Box
          className={`dropdown-menu ${customStyles.menuClass || ""}`}
          sx={{
            top: menuPlacement === "above" ? "unset" : "100%",
            bottom: menuPlacement === "above" ? "100%" : "unset",
            marginBottom: menuPlacement === "above" ? "8px" : "0",
            marginTop: menuPlacement === "above" ? "0" : "4px",
            ...customStyles.menu,
          }}
        >
          {options.map((option, index) => (
            <React.Fragment key={option.value}>
              <Box
                className={`dropdown-item  ${
                  value === option.value ? "selected" : ""
                }`}
                style={customStyles.option}
                onClick={() => handleOptionClick(option.value)}
                role="menuitem"
              >
                {option.icon && (
                  <img
                    src={require(`../assets/${option.icon}`)}
                    alt={`${option.label}-icon`}
                    className="dropdown-icon"
                  />
                )}
                <Typography
                  variant="body1"
                  className={`dropdown-label ${customStyles.optionClass || ""}`}
                >
                  {option.label}
                </Typography>
              </Box>
              {/* Only add divider if it's not the last item */}
              {index !== options.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </Box>
      )}
    </Box>
  );
};

CustomDropdown.propTypes = {
  label: PropTypes.string.isRequired, // Label is required
  value: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: (PropTypes.string as any).isRequired, // Icon is optional
    }).isRequired
  ).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default CustomDropdown;
